/* VIPERCAT pure logic (no DOM). ES module; components import it as '@vc/logic' (the import map of snippets/scripts.liquid).
   Spec: docs/superpowers/specs/2026-09-27-vipercat-storefront-design.md — D6 availability, D7 custom
   measurements, D9 sets, D11 height bands (NCD-RisC 2020, European women 15–30). */

export const HEIGHT_BANDS = Object.freeze([
  { key: 'lo', label: 'Under 160' }, { key: 'm1', label: '160–165' },
  { key: 'm2', label: '165–170' }, { key: 'hi', label: 'Over 170' },
]);
export const RULER = Object.freeze({ min: 150, max: 185 });

const num = v => (v === null || v === undefined || v === '' ? NaN : Number(v));

export function clampHeight(h) {
  const n = Math.round(num(h));
  return Number.isFinite(n) ? Math.min(RULER.max, Math.max(RULER.min, n)) : null;
}

export function bandOf(h) {
  const n = num(h);
  if (!Number.isFinite(n) || n <= 0) return null;
  if (n < 160) return 'lo';
  if (n <= 165) return 'm1';
  if (n <= 170) return 'm2';
  return 'hi';
}

const heights = p => (p.wearers || []).map(w => num(w.height_cm)).filter(Number.isFinite);

export function bandCounts(portraits) {
  const c = { lo: 0, m1: 0, m2: 0, hi: 0 };
  for (const p of portraits) for (const k of new Set(heights(p).map(bandOf))) if (k) c[k]++;
  return c;
}

export function filterPortraits(portraits, st = {}) {
  const any = v => !v || v === 'all';
  return portraits.filter(p =>
    (any(st.source) || p.source === st.source) &&
    (any(st.colourway) || p.colourway === st.colourway) &&
    (any(st.piece) || (p.pieces || []).includes(st.piece)) &&
    (any(st.wearer) || (p.wearers || []).some(w => w.id === st.wearer)) &&
    (any(st.band) ||
      (st.band === 'you' ? heights(p).some(h => Math.abs(h - st.you) <= (st.radius ?? 5))
                         : heights(p).some(h => bandOf(h) === st.band))));
}

const keys = a => [...new Set(a)].sort();

export function matchSet(selected, sets) {
  const s = keys(selected);
  return sets.find(x => { const m = keys(x.members); return m.length === s.length && m.every((k, i) => k === s[i]); }) || null;
}

export function nearestSet(selected, sets) {
  const s = new Set(selected);
  if (!s.size) return null;
  let best = null;
  for (const x of sets) {
    const m = new Set(x.members);
    if (![...s].every(k => m.has(k))) continue;
    const missing = [...m].filter(k => !s.has(k));
    if (!missing.length) continue;
    if (!best || missing.length < best.missing.length) best = { set: x, missing };
  }
  return best;
}

export function lookTotal(selected, pieces, sets) {
  const by = new Map(pieces.map(p => [p.key, p]));
  const sum = selected.reduce((t, k) => t + (by.get(k)?.price ?? 0), 0);
  const set = matchSet(selected, sets);
  return { sum, price: set ? set.price : sum, saving: set ? sum - set.price : 0, set };
}

export const AVAILABILITY_LABEL = Object.freeze({
  ready: 'Ready to ship', 'made-to-order': 'Made to order · 2–4 weeks', 'sold-out': 'Sold out',
});

/* `allMadeToOrder` is the theme setting vc_all_made_to_order (every piece is made to order, operator 2026-09-30): an available variant is then made to
   order whatever its stock, and only a variant that is not available is sold out. Snippets/vc-availability.liquid is the same rule. */
export function availability(v, { allMadeToOrder = false } = {}) {
  if (!v || !v.available) return 'sold-out';
  if (allMadeToOrder) return 'made-to-order';
  if (v.inventoryManagement === 'shopify' && v.inventoryPolicy === 'continue' && num(v.inventoryQuantity) <= 0) return 'made-to-order';
  return 'ready';
}

export const MEASURE_FIELDS = Object.freeze(['bust', 'waist', 'hip', 'skirt', 'height']);
export const MEASURE_RANGES_CM = Object.freeze({ bust: [65, 140], waist: [50, 130], hip: [70, 150], skirt: [30, 110], height: [140, 200] });

export function toCm(value, unit = 'cm') {
  const n = Number(String(value).trim().replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return NaN;
  return unit === 'in' ? Math.round(n * 2.54 * 10) / 10 : n;
}

/** A measure's accepted range in the unit shown, [low, high]: centimetres as MEASURE_RANGES_CM has them, inches rounded inward (the
 *  low up, the high down) so no figure shown is outside the range. The one place the inch figures are made: validateMeasurements words
 *  its message from it ("Between 28 and 59 in") and the Custom measurements page prints the same range beside each measure.
 *  New: the page needs the range before anything is typed. */
export function measureRange(field, unit = 'cm') {
  const [lo, hi] = MEASURE_RANGES_CM[field];
  return unit === 'in' ? [Math.ceil(lo / 2.54), Math.floor(hi / 2.54)] : [lo, hi];
}

export function validateMeasurements(values = {}, unit = 'cm', fields = MEASURE_FIELDS) {
  const errors = {}, cm = {};
  for (const f of fields) {
    const raw = values[f];
    if (raw === undefined || raw === null || String(raw).trim() === '') { errors[f] = 'Required'; continue; }
    const c = toCm(raw, unit);
    const [lo, hi] = MEASURE_RANGES_CM[f];
    if (!Number.isFinite(c)) errors[f] = 'Enter a number';
    else if (c < lo || c > hi) { const [a, b] = measureRange(f, unit === 'in' ? 'in' : 'cm'); errors[f] = `Between ${a} and ${b} ${unit === 'in' ? 'in' : 'cm'}`; }
    else cm[f] = c;
  }
  return { ok: Object.keys(errors).length === 0, errors, cm };
}

export function hydratePortraits(list) {
  return (list || []).map(p => {
    const det = new Map((Array.isArray(p.details) ? p.details : []).map(d => [d.wearer, d]));
    return { ...p, wearers: (p.wearers || []).map(w => {
      const d = det.get(w.id);
      return d ? { ...w, size: d.size, fit: d.fit } : { ...w };
    }) };
  });
}

const MOODS = new Set(['noir', 'sweet']);
export function shouldVeil(fromMood, toMood, reducedMotion = false) {
  return !reducedMotion && MOODS.has(fromMood) && MOODS.has(toMood) && fromMood !== toMood;
}

/** The mood a click on an `a[data-mood]` takes the lace veil to (assets/vc-transition.js; spec D14, task-16-brief "Safety"), or
 *  null when the browser just follows the link. Null for: a click something already prevented (the turntable's first click on its
 *  chapter link, which plays its push-in and then clicks the same link for real: assets/vc-turntable.js `follow()`), a modifier or
 *  a button other than the main one, a link that opens elsewhere (`target` other than _self) or downloads, an in-page `#` link,
 *  another origin (`javascript:` and `mailto:` are one: their origin is "null"), the page itself; then `shouldVeil` decides on the moods
 *  and reduced motion. New signature: `shouldVeil` knows only moods, and the rest is the DOM's, handed in as plain values so a node
 *  test can run every case (Node has no `@vc/logic`, so the module cannot be imported there).
 *  click: the event (defaultPrevented, button, metaKey, ctrlKey, shiftKey, altKey); link: { mood: its data-mood, href: the resolved
 *  URL (`a.href`), attr: the href attribute, target, download: has the attribute }; page: { href: location.href, mood: html[data-mood],
 *  reducedMotion }. ref: https://developer.mozilla.org/en-US/docs/Web/API/URL */
export function veilTarget(click, link, page) {
  if (click.defaultPrevented || click.button !== 0 || click.metaKey || click.ctrlKey || click.shiftKey || click.altKey) return null;
  if (link.download || (link.target && link.target !== '_self')) return null;
  if (!link.attr || link.attr.startsWith('#')) return null;
  let to;
  let here;
  try { to = new URL(link.href); here = new URL(page.href); } catch (_) { return null; }
  if (to.origin !== here.origin || (to.pathname === here.pathname && to.search === here.search)) return null;
  return shouldVeil(page.mood, link.mood, page.reducedMotion) ? link.mood : null;
}

/* ---------------------------------------------------------------- the look builder (Task 12, spec D9 B)
   The rules <vc-look-builder> (assets/vc-look-builder.js) follows, on the JSON sections/vc-look-builder.liquid prints. */

const group = (cents, precision, thousands, decimal) => {
  const [a, b = ''] = (cents / 100).toFixed(precision).split('.');
  const whole = a.replace(/\d(?=(\d{3})+(?!\d))/g, (d) => d + thousands);
  return precision > 0 ? whole + decimal + b : whole;
};

/** Pence in the shop's money format, as the `money` filter prints it (controller ruling 3; Liquid prices stay Liquid).
 *  Ported from Atelier's assets/facets.js:783-847 (#formatMoney / #formatCents, which "replicated the implementation of
 *  the money liquid filters"): {{amount}} 1,234.56 · {{amount_no_decimals}} 1,235 · {{amount_with_comma_separator}}
 *  1.234,56 · {{amount_no_decimals_with_comma_separator}} 1.235 (Shopify's own naming: no decimals, so no comma). Any
 *  other amount_* placeholder prints as {{amount}}; other placeholders are left as written. New signature: Atelier's is
 *  a private method bound to its facet DOM, and its utilities.js formatMoney parses a money string instead. */
export function formatMoney(cents, format) {
  const n = Number(cents);
  const v = Number.isFinite(n) ? n : 0;
  const fmt = typeof format === 'string' && format ? format : '{{amount}}';
  return fmt.replace(/{{\s*(\w+)\s*}}/g, (all, ph) => {
    if (ph === 'amount_no_decimals') return group(v, 0, ',', '.');
    if (ph === 'amount_with_comma_separator') return group(v, 2, '.', ',');
    if (ph === 'amount_no_decimals_with_comma_separator') return group(v, 0, '.', ',');
    return ph.startsWith('amount') ? group(v, 2, ',', '.') : all;
  });
}

/** A formatted price in four parts, so the carte can set the currency and the pence small beside a Bodoni figure
 *  (prototypes/configurator.html:150, 180: `<i>£</i>59`): the text before the figure, the figure, a decimal part (a "."
 *  or "," and two digits at its end) and the text after it. The parts always join back into the input. New: the
 *  prototype printed whole pounds, and a price in the shop's format carries its pence. */
export function moneyParts(s) {
  const str = String(s ?? '');
  const m = /^(\D*?)(\d(?:[\d.,\s'’]*\d)?)(\D*)$/.exec(str);
  if (!m) return { pre: '', num: str, dec: '', post: '' };
  const d = /[.,]\d{2}$/.exec(m[2]);
  return d ? { pre: m[1], num: m[2].slice(0, d.index), dec: d[0], post: m[3] } : { pre: m[1], num: m[2], dec: '', post: m[3] };
}

const ROMAN = [[1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'], [10, 'X'],
  [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I']];
/** 1 → I … 3999 → MMMCMXCIX, the subtractive form; '' for anything else. New: the prototype's ROMAN list
 *  (prototypes/configurator.html:325) stops at the seven pieces of its demo carte. */
export function roman(n) {
  let v = Math.trunc(Number(n));
  if (!(v >= 1 && v <= 3999)) return '';
  let out = '';
  for (const [k, s] of ROMAN) while (v >= k) { out += s; v -= k; }
  return out;
}

/** A title with the colourway's name dropped from its front, as the cards, the sets and the bag print it
 *  (snippets/vc-product-card.liquid:57-67): "Velvet Rouge Short Cape" → "Short Cape". Only a whole word is dropped, and
 *  never the whole title. */
export function shortTitle(title, colourway) {
  const t = String(title ?? ''), c = String(colourway ?? '');
  if (!c || !t.startsWith(c) || !/^\s/.test(t.slice(c.length))) return t;
  return t.slice(c.length).trim() || t;
}

/** The carte's pieces and the sets it may offer, from the section's JSON (task-12-brief.md "JS"): a piece is an entry
 *  with members null and a piece key, keyed by that key (the first product of a key wins); a set is an entry with
 *  members, keyed by its handle, offered only when complete — no member Liquid returned as nil (controller ruling 1) —
 *  and when every member is a piece on this carte, since a set the carte cannot compose can be neither matched nor
 *  suggested. New signature: matchSet / nearestSet / lookTotal take pieces and sets in exactly this shape. */
export function lookParts(items) {
  const list = Array.isArray(items) ? items.filter((it) => it && typeof it === 'object') : [];
  const by = new Map();
  for (const it of list) {
    if (it.members === null && typeof it.piece === 'string' && it.piece && !by.has(it.piece)) by.set(it.piece, { ...it, key: it.piece });
  }
  const sets = list.filter((it) => Array.isArray(it.members) && it.members.length && it.complete === true
    && it.members.every((k) => typeof k === 'string' && by.has(k))).map((it) => ({ ...it, key: it.handle }));
  return { pieces: [...by.values()], sets };
}

/** lookTotal for the selection, and, when it is no set yet, the nearest set it could complete with what completing it
 *  saves (the members' prices less the set's; the suggestion omits "and save" at 0 or less, controller ruling 5). New
 *  signature: lookTotal reports a saving on an exact match only, and the suggestion needs the nearest set's. */
export function lookStatus(selected, pieces, sets) {
  const t = lookTotal(selected, pieces, sets);
  const near = t.set || !selected.length ? null : nearestSet(selected, sets);
  const by = new Map(pieces.map((p) => [p.key, p]));
  const nearSaving = near ? near.set.members.reduce((s, k) => s + (by.get(k)?.price ?? 0), 0) - near.set.price : 0;
  return { ...t, near, nearSaving };
}

/** The multibuy rule (operator, 2026-09-30: three big pieces or more, 10 % off each; tools/multibuy.py is its source and the
 *  Shopify automatic discount applies it). `lines` are { price (pence), eligible, quantity }; only ELIGIBLE units count toward
 *  `min` (help.shopify.com/en/manual/discounts/discount-types/percentage-fixed-amount) and, once there are enough, each one
 *  is discounted by round(price x percent / 100) per unit, half up, the way Shopify rounds (a preview: the bag's total is
 *  Shopify's). `needed` is how many more eligible units reach `min` (0 once it qualifies); `total` is the sum less the discount.
 *  New: the rule has no counterpart in the prototype; lookTotal prices exact sets only. */
export function multibuy(lines, { min = 3, percent = 10 } = {}) {
  let count = 0, sum = 0, each = 0;
  for (const l of Array.isArray(lines) ? lines : []) {
    const price = Math.max(0, Math.round(num(l && l.price)) || 0);
    const quantity = l && l.quantity !== undefined ? Math.max(0, Math.trunc(num(l.quantity)) || 0) : 1;
    sum += price * quantity;
    if (l && l.eligible) { count += quantity; each += Math.round(price * percent / 100) * quantity; }
  }
  const qualifies = count >= min;
  const discount = qualifies ? each : 0;
  return { count, qualifies, discount, total: sum - discount, needed: qualifies ? 0 : Math.max(0, min - count) };
}

/** The Size value spec D7 prices at +£15 (global constraints: "The Size option value 'Custom measurements'"); chosen on
 *  the product page, with its measurements. Exported for the product page (Task 13), which tells the custom variant by it. */
export const CUSTOM_SIZE = 'Custom measurements';
const optionAt = (piece, name) => (Array.isArray(piece.options) ? piece.options : []).findIndex((o) => String(o).trim().toLowerCase() === name);
const valuesOf = (v) => (Array.isArray(v.options) ? v.options : []);

/** The Size and Print values the pieces offer, each in the order their variants first give it, Custom measurements
 *  left out (controller ruling 4); `custom` says whether any piece offers it, on its own page (the brief). Options are
 *  found by name, never by position (ruling 2). New: the prototype had no sizes (it added whole looks). */
export function fitValues(pieces) {
  const sizes = [], prints = [];
  let custom = false;
  for (const p of pieces) {
    const si = optionAt(p, 'size'), pi = optionAt(p, 'print');
    for (const v of p.variants || []) {
      const o = valuesOf(v);
      if (si >= 0 && o[si] === CUSTOM_SIZE) custom = true;
      else if (si >= 0 && o[si] != null && !sizes.includes(o[si])) sizes.push(o[si]);
      if (pi >= 0 && o[pi] != null && !prints.includes(o[pi])) prints.push(o[pi]);
    }
  }
  return { sizes, prints, custom };
}

/** The piece's variant in `size` / `print`, matched by option name (controller ruling 2): an option the product does not
 *  have is not matched on, so a product without Print matches on Size alone, and one with neither takes its first
 *  variant. null when it has no such variant. */
export function variantFor(piece, size, print) {
  const si = optionAt(piece, 'size'), pi = optionAt(piece, 'print');
  const vs = Array.isArray(piece.variants) ? piece.variants : [];
  return vs.find((v) => (si < 0 || valuesOf(v)[si] === size) && (pi < 0 || valuesOf(v)[pi] === print)) || null;
}

/** The selected pieces resolved in `size` / `print`: ok with one line {piece, variant} per piece when every piece has an
 *  available variant there (a made-to-order variant is available: global constraints); else the pieces in the way,
 *  soldOut (a variant that is not available) and missing (no such variant). `state` is the look's availability key
 *  (AVAILABILITY_LABEL): made-to-order when any line is, since the look ships with its last piece, else ready; each
 *  variant's own state is snippets/vc-availability.liquid's (spec D6). */
export function fitCheck(pieces, size, print) {
  const lines = [], soldOut = [], missing = [];
  for (const p of pieces) {
    const v = variantFor(p, size, print);
    if (!v) missing.push(p);
    else if (!v.available) soldOut.push(p);
    else lines.push({ piece: p, variant: v });
  }
  const ok = lines.length > 0 && !soldOut.length && !missing.length;
  const state = ok ? (lines.some((l) => l.variant.state === 'made-to-order') ? 'made-to-order' : 'ready') : null;
  return { ok, lines, soldOut, missing, state };
}

const andList = (a) => (a.length < 2 ? a.join('') : `${a.slice(0, -1).join(', ')} and ${a[a.length - 1]}`);
/** The line that says why the CTA waits (controller ruling 4): "Boned Corset is sold out in XL · Printed"; several
 *  pieces "A, B and C are …"; a piece with no such variant "is not made in …"; '' when nothing is in the way. */
export function fitMessage(soldOut, missing, label) {
  const say = (names, what) => (names.length ? `${andList(names)} ${names.length > 1 ? 'are' : 'is'} ${what} in ${label}` : '');
  return [say(soldOut, 'sold out'), say(missing, 'not made')].filter(Boolean).join('; ');
}

/* ---------------------------------------------------------------- the product page (Task 13, spec D6 / D7)
   The rules <vc-product> (assets/vc-product.js) follows, on the JSON sections/vc-product.liquid prints. */

/** What the product page's Add to bag says and whether it can act (task-13-brief.md items 5–7; the MRS button states,
 *  D:/mrs-studios-shopify/theme/sections/mrs-product-detail.liquid:185-187: "Sold Out" disabled when the variant is not
 *  available). `variant` is the chosen one (vc-logic availability(): made to order is available), or null when none
 *  is resolved; `needsSize`: the product has a Size and none is chosen; `custom`: Custom measurements is chosen, which
 *  waits for its measurements until `measured` says the atelier module's form is valid (validateMeasurements ok, Task 13b);
 *  `soldOut`: no variant of the product is available, so there is no size to ask for. `wait` names why the button waits
 *  (null when it can add). Waiting for measurements is not `disabled`, as waiting for a size is not: the press is what shows
 *  the customer which fields are missing (task-13b brief, Step 5). New signature: fitCheck / fitMessage answer for several
 *  pieces at once, and the product page has states the carte has not (Custom measurements, a sold-out label on the button
 *  itself). */
export function productCta({ variant = null, needsSize = false, custom = false, soldOut = false, measured = false } = {}) {
  if (soldOut || (variant && !variant.available)) return { label: 'Sold out', disabled: true, wait: 'sold-out' };
  if (needsSize) return { label: 'Select a size', disabled: false, wait: 'size' };
  if (!variant) return { label: 'Unavailable', disabled: true, wait: 'missing' };
  if (custom && !measured) return { label: 'Enter your measurements', disabled: false, wait: 'measurements' };
  return { label: 'Add to bag', disabled: false, wait: null };
}

/* ---------------------------------------------------------------- the atelier (Task 13b, spec D7)
   The rules <vc-atelier> (assets/vc-atelier.js) follows. */

/** The line-item properties of a custom-measured piece, exactly (task-13-brief.md "Produces"), by MEASURE_FIELDS key. */
export const MEASURE_PROPERTY = Object.freeze({ bust: 'Bust (cm)', waist: 'Waist (cm)', hip: 'Hip (cm)', skirt: 'Skirt length (cm)', height: 'Height (cm)' });

const oneDecimal = (n) => Math.round(n * 10) / 10;

/** validateMeasurements plus the cart line's properties: every value in cm to a millimetre, as a string (add.js takes strings),
 *  and `_unit`, the unit the customer typed in (a key starting with "_" is hidden in the bag: Shopify's convention). `properties`
 *  is null while anything is wrong; `errors` are validateMeasurements' own ("Required", "Enter a number", "Between 70 and 150 cm").
 *  New: validateMeasurements answers in numbers, and the line needs these keys. */
export function measureProperties(values = {}, unit = 'cm') {
  const u = unit === 'in' ? 'in' : 'cm';
  const v = validateMeasurements(values, u);
  if (!v.ok) return { ...v, properties: null };
  const properties = {};
  for (const f of MEASURE_FIELDS) properties[MEASURE_PROPERTY[f]] = String(oneDecimal(v.cm[f]));
  properties._unit = u;
  return { ...v, properties };
}

/** A typed measurement moved to the other unit, to one decimal without a trailing zero (86.4 cm and 34 in; 166.4 cm and 65.5 in),
 *  so a round trip through the unit toggle loses nothing worth a millimetre. Text that is not a positive number is returned as
 *  typed. New: the prototype (atelier.html:677) rounded to the half unit, which changes what the customer typed. */
export function convertMeasure(value, from, to) {
  const raw = String(value ?? '').trim();
  if (!raw || from === to) return raw;
  const n = Number(raw.replace(',', '.'));
  if (!Number.isFinite(n) || n <= 0) return raw;
  return String(oneDecimal(to === 'in' ? n / 2.54 : n * 2.54));
}

/* ---------------------------------------------------------------- the Worn card's rules (Task 13b)
   The framing, caption and accessible name of a Worn portrait: moved out of sections/vc-worn-strip.liquid's inline module
   (Task 11 carry-over) so the strip, its height chips and the Worn album (Task 17) share one copy. Ported from
   prototypes/worn-a.html (framing 1802-1812 and 1029-1046, caption 926-948, name 983-987), unchanged but for the one fix
   noted on wornLabel. The markup they return carries the caller's BEM block as `cls` (the strip's by default). */

export const WORN_SOURCE = Object.freeze({ atelier: 'Atelier', muse: 'Muse', client: 'Client' }); // worn-a.html:853-855

/** HTML-escape text going into markup: & < > and the double quote. */
export const escapeHtml = (s) => String(s ?? '').replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

const pad2 = (n) => (n < 10 ? '0' : '') + n;
const sizeOf = (q) => q.size || ''; // the size worn; the made-to-measure size keeps its D7 name, "Custom measurements"
const wearerNames = (p) => (p.wearers || []).map((q) => q.name).filter(Boolean);
const CROP0 = { top: null, bottom: null, x: 50 }; // worn-a.html:863: framed centred

/** The photograph's frame (worn-a.html:1802-1812): the trimmed print's ratio picks the nearest opening, 1 : 1, 3 : 4 or 2 : 3
 *  (`ar`; a square one is a cushion, the rest arches), with the trim `t`, the trimmed size `ew` × `eh` and the crop `cut`
 *  normalised. `p` is a portrait from the Worn JSON with `w`, `h` and `crop`. */
export function wornFrame(p) {
  const c = p.crop && typeof p.crop === 'object' ? p.crop : CROP0;
  const t = num(c.trim) || 0, ew = p.w * (1 - 2 * t), eh = p.h * (1 - 2 * t), r = ew / eh;
  const ar = [1, 0.75, 2 / 3].reduce((a, b) => (Math.abs(b - r) < Math.abs(a - r) ? b : a));
  return { ...p, ar, t, ew, eh, cut: { top: c.top ?? null, bottom: c.bottom ?? null, x: c.x ?? null, left: c.left ?? null, right: c.right ?? null } };
}

/** worn-a.html:1029-1046, geo(): fit the head-to-shoe span inside the opening, never distort. Units: the opening's height is 1
 *  and its width `A` (its ratio); the print is drawn at height H, width r·H. Returns the image's left, top, width and height in
 *  percent of the opening. `p` is a wornFrame() result. */
export function wornGeometry(p, A) {
  const c = p.cut, t = p.t, k = 1 - 2 * t, r = p.ew / p.eh;
  const e = (v) => (v / 100 - t) / k;
  const top = c.top == null ? 0 : Math.max(0, e(c.top) - 0.05);
  const bot = c.bottom == null ? 1 : Math.min(1, e(c.bottom) + 0.025);
  const cx = e(c.x == null ? 50 : c.x);
  let hl, hr;
  if (c.left != null) { hl = Math.max(0, e(c.left) - 0.02); hr = Math.min(1, e(c.right) + 0.02); } else { hl = Math.max(0, cx - 0.2); hr = Math.min(1, cx + 0.2); }
  const cover = Math.max(1, A / r), Hv = 1 / (bot - top), Hh = A / (r * (hr - hl));
  const H = Math.max(cover, Math.min(Hv, Hh, cover * 1.15));
  const W = r * H, sx = c.left != null ? (hl + hr) / 2 : cx;
  let y = (bot - top) * H <= 1 ? 0.5 - (top + bot) / 2 * H : -top * H;
  y = Math.min(0, Math.max(1 - H, y));
  const x = Math.min(0, Math.max(A - W, A / 2 - sx * W));
  const fw = W / k, fh = H / k;
  return { l: (x - t * fw) / A * 100, tp: (y - t * fh) * 100, w: fw / A * 100, h: fh * 100 };
}

/** The caption, the measurement first (worn-a.html:926-948, 664-673): one wearer "165 cm · S"; a group pairs each height with its
 *  own size, heights ascending, "157 XS · 168 M", or "both S" when the sizes agree. HTML: `<small>` units and the
 *  `${cls}__pr` / `${cls}__pd` parts. */
export function wornMeasure(wearers, cls = 'vc-worn-strip') {
  const ppl = Array.isArray(wearers) ? wearers : [];
  const PD = `<i class="${cls}__pd">·</i>`;
  const withH = ppl.filter((q) => Number.isFinite(num(q.height_cm)));
  if (ppl.length > 1 && withH.length > 1) {
    const a = withH.slice().sort((x, y) => num(x.height_cm) - num(y.height_cm)), shown = a.slice(0, 3), more = a.length - shown.length;
    if (a.every((q) => sizeOf(q) === sizeOf(a[0])) && sizeOf(a[0])) {
      return shown.map((q) => escapeHtml(q.height_cm)).join(PD) + '<small>cm</small>' + PD + (a.length === 2 ? 'both ' : 'all ') + escapeHtml(sizeOf(a[0]));
    }
    return shown.map((q) => `<span class="${cls}__pr">` + escapeHtml(q.height_cm) + '<small>' + escapeHtml(sizeOf(q)) + '</small></span>').join(PD) + (more ? '<small>+' + more + '</small>' : '');
  }
  const q = withH[0] || ppl[0] || {}, h = num(q.height_cm), z = sizeOf(q);
  return [Number.isFinite(h) ? escapeHtml(h) + '<small>cm</small>' : '', escapeHtml(z)].filter(Boolean).join(PD);
}

/** Who wears it (worn-a.html:922): the atelier gets its plate number, `i` the card's place in the album, from 0; a muse or a
 *  client her own name (a group "A, B & C"). HTML, escaped. */
export function wornWho(p, i, cls = 'vc-worn-strip') {
  if (p.source === 'atelier') return `<span class="${cls}__no">No. ` + pad2(i + 1) + '</span> · Fitting';
  const n = wearerNames(p);
  return escapeHtml(n.length < 2 ? n.join('') : n.slice(0, -1).join(', ') + ' & ' + n[n.length - 1]);
}

/** The card's accessible name (worn-a.html:983-987): who, each wearer's height and size, the pieces worn. Plain text.
 *  Changed in the move: a wearer of a group whose height and size are both unknown now adds nothing; the inline version
 *  said "Sam wears M; Ada ; Bo ," (a name followed by an empty statement). */
export function wornLabel(p, i) {
  const ws = p.wearers || [];
  const whoL = p.source === 'atelier' ? 'No. ' + pad2(i + 1) + ', atelier fitting on ' + andList(wearerNames(p)) : andList(wearerNames(p)) + ', ' + (WORN_SOURCE[p.source] || '').toLowerCase();
  const body = ws.map((q) => {
    const h = num(q.height_cm), z = sizeOf(q);
    const facts = [Number.isFinite(h) ? h + ' centimetres' : '', z ? 'wears ' + z : ''].filter(Boolean).join(', ');
    return facts && (ws.length > 1 && q.name ? q.name + ' ' : '') + facts;
  }).filter(Boolean).join('; ');
  const worn = andList((p.products || []).map((x) => x.title).filter(Boolean));
  return [whoL, body, worn].filter(Boolean).join(', ') + '. See it in the Worn album.';
}

/** The height filter of a product page's Worn strip (D11 bands): "All" and each band that holds a portrait, with the counts
 *  bandCounts gives; [] when there is nothing to choose between (fewer than two portraits, or fewer than two bands in use).
 *  Each `key` is what filterPortraits takes as `band` ('all' for none). New: the bands are D11's; which of them to offer is
 *  the strip's own rule. */
export function heightChips(portraits) {
  const list = Array.isArray(portraits) ? portraits : [];
  const counts = bandCounts(list);
  const bands = HEIGHT_BANDS.filter((b) => counts[b.key] > 0);
  if (list.length < 2 || bands.length < 2) return [];
  return [{ key: 'all', label: 'All', count: list.length }, ...bands.map((b) => ({ key: b.key, label: b.label, count: counts[b.key] }))];
}

/* ---------------------------------------------------------------- the set page (Task 14, spec D9 A)
   The rules <vc-set-product> (assets/vc-set-product.js) follows, on the rows sections/vc-set-product.liquid prints. */

/** The set page's pieces resolved (Task 14c): a set is added as its PIECES, one cart line per member, each the member's variant
 *  in the size chosen for it and the set's one print, found by option NAME (variantFor); the Shopify discount then prices them
 *  (tools/multibuy.py). `members` are the rows: { title, size, choice, options, variants }, `choice` saying the piece has a
 *  Size to choose (a piece without one is resolved at once). `lines` are { member, variant } for every member that has a
 *  purchasable variant; `soldOut` / `missing` the members whose variant is not available / does not exist in that size and print;
 *  `reason` says why in the look builder's words ("Boned Corset is sold out in XL · Printed"); `state` is the set's availability
 *  key (AVAILABILITY_LABEL) once EVERY member resolves to an available variant — made-to-order when any is, since the set ships
 *  with its last piece, else ready — and null before that. New: the look builder chooses one size for all its pieces (fitCheck);
 *  a set chooses one for each. */
export function setFit(members, print) {
  const lines = [], soldOut = [], missing = [];
  let waiting = false;
  for (const m of Array.isArray(members) ? members : []) {
    if (m.choice && !m.size) { waiting = true; continue; }
    const v = variantFor(m, m.size, print);
    if (!v) missing.push(m); else if (!v.available) soldOut.push(m); else lines.push({ member: m, variant: v });
  }
  const label = (m) => [m.size, optionAt(m, 'print') >= 0 ? print : ''].filter(Boolean).join(' · ');
  const reason = [...soldOut.map((m) => fitMessage([m.title], [], label(m))), ...missing.map((m) => fitMessage([], [m.title], label(m)))].join('; ');
  const all = !waiting && !soldOut.length && !missing.length && lines.length > 0;
  const state = all ? (lines.some((l) => l.variant.state === 'made-to-order') ? 'made-to-order' : 'ready') : null;
  return { lines, soldOut, missing, reason, state };
}

/** What the set page's Add to bag says and whether it can act (task-14-brief.md "Layout": "The Add button is disabled until
 *  every member has a size"). `members` are the rows: { title, size, choice }, `choice` saying the piece has a Size to choose
 *  (a piece without one never holds the button back); `soldOut` / `missing`: setFit found a chosen piece sold out / not made
 *  in that size and print. No member to show (every reference Liquid returned as nil) leaves nothing to compose. Unlike
 *  productCta's "Select a size", waiting here is `disabled` (the brief's word); the label says why. New signature: productCta
 *  answers for one piece. */
export function setCta({ members = [], soldOut = false, missing = false } = {}) {
  if (soldOut) return { label: 'Sold out', disabled: true, wait: 'sold-out' };
  const rows = Array.isArray(members) ? members : [];
  if (!rows.length || missing) return { label: 'Unavailable', disabled: true, wait: 'missing' };
  if (rows.some((m) => m && m.choice && !m.size)) return { label: 'Select your sizes', disabled: true, wait: 'size' };
  return { label: 'Add to bag', disabled: false, wait: null };
}

/* ---------------------------------------------------------------- the turntable (Task 15, spec D13; prototype rose-b v4)
   The data rules <vc-turntable> (assets/vc-turntable.js) follows, on the JSON sections/vc-turntable.liquid prints. What is
   here has no DOM: the projection and the animation stay in the component (controller ruling, Task 15). */

/** The prototype's art for each colourway, keyed by colourway handle (the prototype's `id`): the CW table of
 *  prototypes/rose-b.html:455-462, the ten keys the metaobject's `art` JSON may carry. `hem` is the hem row and `hx` its
 *  centre, in pixels of the cutout; `f` a scale tweak; `expo` / `con` the exposure and contrast that even the cutouts out;
 *  `stand` the neck stand [x0, x1, bottom at the centre, collar top] in cutout pixels; `light` the chapter's light (rgb);
 *  `halo` its strength; `amb` the room level while this figure is in front; `cr` a right crop in pixels (Porcelain Blue's 4;
 *  the others have none). The values are measured on the prototype's cutout files (839x1100 and so on). A test reads the
 *  prototype's own table and compares it with this one (tests/js/turntable-logic.test.mjs). */
export const TURNTABLE_ART = Object.freeze({
  'rose-blanche':   Object.freeze({ hem: 1084, hx: 432, f: 1,    expo: 0.9,  con: 1,    stand: [352, 460, 92, 38],  light: [238, 198, 204], halo: 1,    amb: 1 }),
  'porcelain-blue': Object.freeze({ hem: 1021, hx: 441, f: 1.04, expo: 0.84, con: 1,    stand: [365, 470, 50, 26],  cr: 4, light: [232, 206, 214], halo: 1, amb: 1 }),
  'velvet-rouge':   Object.freeze({ hem: 1092, hx: 432, f: 1,    expo: 1,    con: 1,    stand: [364, 478, 84, 36],  light: [240, 222, 198], halo: 0.95, amb: 0.9 }),
  'midnight':       Object.freeze({ hem: 1096, hx: 432, f: 1,    expo: 1.16, con: 1.04, stand: [354, 464, 104, 40], light: [214, 222, 240], halo: 1.55, amb: 0.84 }),
  'pierrot':        Object.freeze({ hem: 1096, hx: 431, f: 1,    expo: 0.9,  con: 1,    stand: [353, 461, 98, 38],  light: [236, 230, 220], halo: 1,    amb: 0.9 }),
  'noir':           Object.freeze({ hem: 971,  hx: 447, f: 1.04, expo: 0.84, con: 1.08, stand: [407, 494, 36, 14],  light: [200, 202, 208], halo: 1.2,  amb: 0.74 }),
});

const finite = (v) => typeof v === 'number' && Number.isFinite(v);
const text = (v) => (typeof v === 'string' ? v.trim() : '');
const ROMAN_VALUE = { I: 1, V: 5, X: 10, L: 50, C: 100, D: 500, M: 1000 };

/** A roman numeral as a number (I → 1 … VI → 6), in either case; NaN for anything that is not one, including a numeral
 *  not written in the canonical form ("IIII", "VX") and any value that is not a string. New: roman() goes the other way. */
export function numeralValue(n) {
  if (typeof n !== 'string') return NaN;
  const s = n.trim().toUpperCase();
  if (!/^[IVXLCDM]+$/.test(s)) return NaN;
  let v = 0;
  for (let i = 0; i < s.length; i++) {
    const a = ROMAN_VALUE[s[i]], b = ROMAN_VALUE[s[i + 1]] || 0;
    v += a < b ? -a : a;
  }
  return roman(v) === s ? v : NaN;
}

/** The list in numeral order, I–VI, as a new array (spec: the series' colourways "ordered by numeral I–VI"). An item with no
 *  usable numeral goes last, and equal numerals keep the order they came in. `key` names the numeral field. */
export function sortByNumeral(list, key = 'num') {
  if (!Array.isArray(list)) return [];
  const rank = (x) => { const n = numeralValue(x ? x[key] : undefined); return Number.isNaN(n) ? Infinity : n; };
  return list.map((x, i) => [x, i, rank(x)]).sort((a, b) => (a[2] === b[2] ? a[1] - b[1] : a[2] - b[2])).map((t) => t[0]);
}

/** "Noir" or "Sweet" for the metaobject's lowercase `mood` (noir | sweet); any other value is noir, as snippets/vc-mood.liquid reads it. */
export function moodLabel(mood) {
  return String(mood ?? '').trim().toLowerCase() === 'sweet' ? 'Sweet' : 'Noir';
}

/** The fabric text (a multi-line text field) as its lines: the parts the prototype joins with its gold dot
 *  (prototypes/rose-b.html:685, `d.fabric.join(dot)`). One line, as the seed has, is one part and no dot; blank lines go. */
export function fabricParts(value) {
  return String(value ?? '').split(/\r\n|\r|\n/).map((s) => s.trim()).filter(Boolean);
}

/** The colourway's collection: "<series handle>-<colourway handle>" (tools/seed_demo.py collections(); the same pattern as
 *  sections/vc-turntable.liquid and vc-chapter-header.liquid print). '' without a colourway handle. */
export function chapterUrl(seriesHandle, handle) {
  const s = text(seriesHandle), h = text(handle);
  return h ? `/collections/${s ? `${s}-` : ''}${h}` : '';
}

const ART_POSITIVE = ['hem', 'hx', 'f', 'expo', 'con', 'halo', 'amb'];
/** Whether an `art` value can be used as it is: a number the figure's maths can divide by and scale with, or the array of
 *  numbers the key takes. Anything else falls back to the default for that key, so art can never blank the stage. */
function usableArt(key, v) {
  if (key === 'stand') return Array.isArray(v) && v.length === 4 && v.every(finite);
  if (key === 'light') return Array.isArray(v) && v.length === 3 && v.every((c) => finite(c) && c >= 0 && c <= 255);
  if (key === 'cr') return finite(v) && v >= 0;
  return ART_POSITIVE.includes(key) && finite(v) && v > 0;
}

/** Values for a handle the prototype has no entry for, from its typical ones: the medians of the six (hem, hem centre and the
 *  stand as fractions of the picture's own size; f, con, halo 1; expo 0.9), and the light by mood — sweet the Rose Blanche
 *  light, noir the Pierrot light (controller ruling); a sweet chapter does not dim the room (amb 1, the noir ones 0.9). */
function genericArt(sweet, w, h) {
  return {
    hem: Math.round(h * 0.993), hx: Math.round(w * 0.515), f: 1, expo: 0.9, con: 1,
    stand: [Math.round(w * 0.43), Math.round(w * 0.555), Math.round(h * 0.08), Math.round(h * 0.034)],
    light: (sweet ? TURNTABLE_ART['rose-blanche'] : TURNTABLE_ART.pierrot).light.slice(), halo: 1, amb: sweet ? 1 : 0.9, cr: 0,
  };
}

/** The art for one colourway (the ten keys of TURNTABLE_ART, `cr` always): the prototype's values for its handle (generic
 *  ones for a handle it does not know), with the metaobject's `art` JSON merged over them key by key, art winning
 *  (controller ruling, Task 15). `art` is the object, or JSON text; anything else, and any unusable value in it, is ignored:
 *  the seeded colourways have no art today, and missing art must never break the stage. `size` is the cutout's {w, h}. */
export function turntableArt(handle, mood, size, art) {
  const w = size && finite(size.w) && size.w > 0 ? size.w : 830, h = size && finite(size.h) && size.h > 0 ? size.h : 1100;
  const known = Object.prototype.hasOwnProperty.call(TURNTABLE_ART, handle) ? TURNTABLE_ART[handle] : null;
  const base = known
    ? { cr: 0, ...known, stand: known.stand.slice(), light: known.light.slice() }
    : genericArt(moodLabel(mood) === 'Sweet', w, h);
  let given = art;
  if (typeof given === 'string') { try { given = JSON.parse(given); } catch (err) { given = null; } }
  if (!given || typeof given !== 'object' || Array.isArray(given)) return base;
  for (const key of Object.keys(base)) {
    if (Object.prototype.hasOwnProperty.call(given, key) && usableArt(key, given[key])) base[key] = Array.isArray(given[key]) ? given[key].slice() : given[key];
  }
  return base;
}

/** The turntable's colourways from the section's JSON (sections/vc-turntable.liquid, the brief's data contract), ready to
 *  draw: in numeral order; each with its mood as noir|sweet and shown as Noir / Sweet, its fabric split into parts, its
 *  art merged over the prototype's (turntableArt), and a link that is a path on this shop (else rebuilt with chapterUrl from
 *  the series handle). A colourway with no id or no usable picture (an empty `img`, or no size) is left out. */
export function prepareTurntable(raw, seriesHandle = '') {
  const rows = [];
  for (const c of Array.isArray(raw) ? raw : []) {
    if (!c || typeof c !== 'object') continue;
    const id = text(c.id), img = text(c.img), w = Number(c.w), h = Number(c.h);
    if (!id || !img || !(w > 0) || !(h > 0) || !Number.isFinite(w) || !Number.isFinite(h)) continue;
    const sweet = moodLabel(c.mood) === 'Sweet', mood = sweet ? 'sweet' : 'noir', url = text(c.url);
    rows.push({
      id, num: text(c.num), name: text(c.name) || id, mood, moodLabel: moodLabel(mood), sweet, fabric: fabricParts(c.fabric),
      desc: text(c.desc), img, srcset: text(c.srcset), sizes: text(c.sizes), w, h, url: /^\/(?!\/)/.test(url) ? url : chapterUrl(seriesHandle, id),
      art: turntableArt(id, mood, { w, h }, c.art),
    });
  }
  return sortByNumeral(rows);
}
